/**
 * NotificationBell — Notification dropdown for nav bars (desktop + mobile)
 *
 * Shows a bell icon with unread count badge.
 * Clicking opens a dropdown with recent notifications grouped by time.
 * Clicking a notification navigates to the relevant page and marks it read.
 * Polls for unread count every 30 seconds.
 *
 * NEW: Supports cross_zone_fork and project_curated notification types
 * with special visual treatments for the cross-discipline magic moments.
 *
 * DEEP LINKING: Notifications that point to /dashboard?project=X will
 * trigger the map to zoom/pan to the project node and open its overlay.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, GitFork, Star, MessageCircle, Users, Target, Zap,
  Check, CheckCheck, X, AlertCircle, Package, ChevronRight,
  Sparkles, Heart, Award, BookOpen, Flame, Eye, Compass,
  FolderTree, ArrowRight
} from 'lucide-react';
import { cn } from '../utils/cn';
import { api } from '../api';

// ============================================================================
// NOTIFICATION TYPE CONFIG — Each type maps to icon, color, verb
// ============================================================================

const NOTIF_CONFIG = {
  project_forked: {
    icon: GitFork,
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/10',
    ring: 'ring-indigo-500/20',
    verb: 'forked your project',
  },
  // ★ NEW — Cross-zone fork: the platform's magic moment
  cross_zone_fork: {
    icon: Compass,
    color: 'text-fuchsia-400',
    bg: 'bg-gradient-to-br from-fuchsia-500/15 to-cyan-500/10',
    ring: 'ring-fuchsia-500/30',
    verb: 'took your project to a new zone',
    isSpecial: true,  // gets extra glow treatment
  },
  // ★ NEW — Project curated: validation that your work was highlighted
  project_curated: {
    icon: FolderTree,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    ring: 'ring-amber-500/20',
    verb: 'featured your project',
  },
  challenge_response: {
    icon: Target,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    ring: 'ring-emerald-500/20',
    verb: 'responded to your challenge',
  },
  challenge_created: {
    icon: Sparkles,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    ring: 'ring-amber-500/20',
    verb: 'posted a challenge',
  },
  collaboration_request: {
    icon: Users,
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
    ring: 'ring-pink-500/20',
    verb: 'wants to collaborate',
  },
  project_comment: {
    icon: MessageCircle,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    ring: 'ring-cyan-500/20',
    verb: 'commented on your project',
  },
  project_starred: {
    icon: Star,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    ring: 'ring-amber-500/20',
    verb: 'starred your project',
  },
  project_liked: {
    icon: Heart,
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
    ring: 'ring-rose-500/20',
    verb: 'liked your project',
  },
  mention: {
    icon: Zap,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    ring: 'ring-purple-500/20',
    verb: 'mentioned you',
  },
  new_follower: {
    icon: Users,
    color: 'text-pink-400',
    bg: 'bg-pink-500/10',
    ring: 'ring-pink-500/20',
    verb: 'started following you',
  },
  milestone: {
    icon: Award,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    ring: 'ring-amber-500/20',
    verb: 'milestone reached',
  },
  peer_review: {
    icon: BookOpen,
    color: 'text-teal-400',
    bg: 'bg-teal-500/10',
    ring: 'ring-teal-500/20',
    verb: 'reviewed your project',
  },
  trending: {
    icon: Flame,
    color: 'text-orange-400',
    bg: 'bg-orange-500/10',
    ring: 'ring-orange-500/20',
    verb: 'your project is trending',
  },
  system: {
    icon: AlertCircle,
    color: 'text-zinc-400',
    bg: 'bg-zinc-500/10',
    ring: 'ring-zinc-500/20',
    verb: '',
  },
};

const DEFAULT_CONFIG = {
  icon: Bell,
  color: 'text-zinc-400',
  bg: 'bg-zinc-500/10',
  ring: 'ring-zinc-500/20',
  verb: '',
};

// ============================================================================
// TIME AGO
// ============================================================================

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ============================================================================
// MINI AVATAR
// ============================================================================

const MiniAvatar = ({ src, username, size = 20 }) => {
  const initials = (username || '?').charAt(0).toUpperCase();

  return (
    <div
      className="rounded-full overflow-hidden bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size }}
    >
      {src ? (
        <img
          src={src}
          alt={username}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : (
        <span className="text-white font-semibold" style={{ fontSize: size * 0.45 }}>
          {initials}
        </span>
      )}
    </div>
  );
};

// ============================================================================
// CROSS-ZONE BADGE — special visual for zone-crossing notifications
// ============================================================================

const ZoneBadge = ({ extraData }) => {
  if (!extraData?.original_zone_label || !extraData?.new_zone_label) return null;

  return (
    <div className="flex items-center gap-1.5 mt-1.5">
      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-300 font-medium">
        {extraData.original_zone_label}
      </span>
      <ArrowRight className="w-3 h-3 text-zinc-600" />
      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 font-medium">
        {extraData.new_zone_label}
      </span>
    </div>
  );
};

// ============================================================================
// SINGLE NOTIFICATION ITEM
// ============================================================================

const NotificationItem = ({ notification, onClickNotif }) => {
  const config = NOTIF_CONFIG[notification.type] || DEFAULT_CONFIG;
  const Icon = config.icon;
  const isSpecial = config.isSpecial;

  return (
    <button
      onClick={() => onClickNotif(notification)}
      className={cn(
        'w-full flex items-start gap-3 px-4 py-3 text-left transition-all duration-150',
        'hover:bg-zinc-800/40 active:bg-zinc-800/60',
        !notification.isRead && 'bg-purple-500/[0.03]',
        // Special glow for cross-zone forks
        isSpecial && !notification.isRead && 'bg-gradient-to-r from-fuchsia-500/[0.04] to-cyan-500/[0.03]'
      )}
    >
      {/* Icon */}
      <div className="relative flex-shrink-0">
        <div className={cn(
          'w-9 h-9 rounded-xl flex items-center justify-center',
          'ring-1 transition-all',
          config.bg, config.ring,
          isSpecial && !notification.isRead && 'shadow-sm shadow-fuchsia-500/20'
        )}>
          <Icon className={cn('w-4 h-4', config.color)} />
        </div>

        {notification.relatedUser?.profilePictureUrl && (
          <div className="absolute -bottom-1 -right-1 ring-2 ring-zinc-900 rounded-full">
            <MiniAvatar
              src={notification.relatedUser.profilePictureUrl}
              username={notification.relatedUser.username}
              size={16}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-[13px] leading-snug',
          notification.isRead ? 'text-zinc-400' : 'text-zinc-200'
        )}>
          {notification.relatedUser && (
            <span className={cn(
              'font-semibold',
              notification.isRead ? 'text-zinc-300' : 'text-white'
            )}>
              {notification.relatedUser.username}
            </span>
          )}{' '}
          {notification.title}
        </p>

        {notification.message && (
          <p className="text-xs text-zinc-500 mt-0.5 line-clamp-1">
            {notification.message}
          </p>
        )}

        {/* Cross-zone badge */}
        {notification.type === 'cross_zone_fork' && (
          <ZoneBadge extraData={notification.extraData} />
        )}

        {/* Project name chip */}
        {notification.relatedProject && notification.type !== 'cross_zone_fork' && (
          <span className="inline-flex items-center gap-1 mt-1 text-[11px] text-zinc-500">
            <Package className="w-3 h-3" />
            {notification.relatedProject.name}
          </span>
        )}

        {/* Curated branch info */}
        {notification.type === 'project_curated' && notification.extraData?.total_projects_in_branch && (
          <span className="inline-flex items-center gap-1 mt-1 text-[11px] text-amber-500/70">
            <FolderTree className="w-3 h-3" />
            {notification.extraData.total_projects_in_branch} projects in collection
          </span>
        )}

        <span className="text-[10px] text-zinc-600 mt-1 block">
          {timeAgo(notification.createdAt)}
        </span>
      </div>

      {/* Unread dot */}
      {!notification.isRead && (
        <div className={cn(
          'w-2 h-2 rounded-full flex-shrink-0 mt-2 shadow-sm',
          isSpecial
            ? 'bg-fuchsia-500 shadow-fuchsia-500/50'
            : 'bg-purple-500 shadow-purple-500/50'
        )} />
      )}
    </button>
  );
};

// ============================================================================
// NOTIFICATION BELL (main export)
// ============================================================================

const NotificationBell = ({ className, isMobile = false }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bellAnimating, setBellAnimating] = useState(false);
  const dropdownRef = useRef(null);
  const pollRef = useRef(null);
  const prevUnreadRef = useRef(0);

  // ---- Fetch unread count ----
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      const newCount = res.data.count || 0;

      if (newCount > prevUnreadRef.current && prevUnreadRef.current !== undefined) {
        setBellAnimating(true);
        setTimeout(() => setBellAnimating(false), 600);
      }
      prevUnreadRef.current = newCount;
      setUnreadCount(newCount);
    } catch (e) {
      // Non-critical
    }
  }, []);

  // ---- Fetch notifications ----
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications', { params: { per_page: 20 } });
      setNotifications(res.data.notifications || []);
    } catch (e) {
      console.error('Failed to load notifications:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // ---- Poll every 30s ----
  useEffect(() => {
    fetchUnreadCount();
    pollRef.current = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchUnreadCount]);

  // ---- Load notifications when dropdown opens ----
  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen, fetchNotifications]);

  // ---- Close on outside click ----
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('touchstart', handleClick, { passive: true });
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [isOpen]);

  // ---- Escape key ----
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) setIsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // ---- Click notification → navigate + mark read ----
  const handleClickNotification = async (notif) => {
    if (!notif.isRead) {
      try {
        await api.post(`/notifications/${notif.id}/read`);
        setNotifications(prev =>
          prev.map(n => n.id === notif.id ? { ...n, isRead: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (e) {
        // Non-critical
      }
    }

    // Navigate with deep-link support
    if (notif.actionUrl) {
      navigate(notif.actionUrl);
    } else if (notif.relatedProjectId) {
      // Deep-link to map with project focus + lineage highlight
      const params = new URLSearchParams();
      params.set('project', notif.relatedProjectId);
      if (notif.type === 'cross_zone_fork' || notif.type === 'project_forked') {
        params.set('highlight', 'lineage');
      }
      navigate(`/dashboard?${params.toString()}`);
    } else if (notif.relatedUserId) {
      navigate(`/profile/${notif.relatedUserId}`);
    }

    setIsOpen(false);
  };

  // ---- Mark all as read ----
  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/read', {});
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error('Failed to mark all read:', e);
    }
  };

  const unreadInView = notifications.filter(n => !n.isRead).length;

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'relative p-2 rounded-xl transition-all duration-200',
          isOpen
            ? 'bg-zinc-800 text-white'
            : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50',
          isMobile && 'p-1.5'
        )}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell
          className={cn(
            'w-5 h-5 transition-transform',
            isMobile && 'w-[18px] h-[18px]',
            bellAnimating && 'animate-wiggle'
          )}
        />

        {unreadCount > 0 && (
          <span className={cn(
            'absolute flex items-center justify-center',
            'min-w-[18px] h-[18px] px-1',
            'text-[10px] font-bold text-white',
            'bg-purple-600 rounded-full',
            'shadow-lg shadow-purple-600/40',
            'transition-all duration-200',
            '-top-0.5 -right-0.5',
            isMobile && '-top-1 -right-1 min-w-[16px] h-[16px] text-[9px]'
          )}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown / Bottom Sheet */}
      {isOpen && (
        <>
          {isMobile && (
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
              onClick={() => setIsOpen(false)}
            />
          )}

          <div className={cn(
            'z-50 bg-zinc-900 border border-zinc-800 shadow-2xl shadow-black/40 overflow-hidden',
            'hidden md:block absolute right-0 top-full mt-2 w-96 rounded-2xl',
            isMobile && 'fixed !block md:!hidden bottom-0 left-0 right-0 rounded-t-2xl max-h-[75vh] border-b-0'
          )}>
            {isMobile && (
              <div className="flex justify-center pt-2 pb-1">
                <div className="w-8 h-1 rounded-full bg-zinc-700" />
              </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800/50">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-white">Notifications</h3>
                {unreadInView > 0 && (
                  <span className="min-w-[20px] h-5 flex items-center justify-center px-1.5 text-[10px] font-bold text-purple-300 bg-purple-500/20 rounded-full">
                    {unreadInView}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                {unreadInView > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 font-medium transition-colors"
                  >
                    <CheckCheck className="w-3 h-3" />
                    Read all
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* List */}
            <div className={cn(
              'overflow-y-auto',
              isMobile ? 'max-h-[60vh]' : 'max-h-[420px]'
            )}>
              {loading && notifications.length === 0 ? (
                <div className="flex justify-center py-10">
                  <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-14 h-14 rounded-2xl bg-zinc-800/30 flex items-center justify-center mb-3 ring-1 ring-zinc-800/50">
                    <Bell className="w-6 h-6 text-zinc-600" />
                  </div>
                  <p className="text-sm text-zinc-400 text-center font-medium">No notifications yet</p>
                  <p className="text-xs text-zinc-600 text-center mt-1 max-w-[220px] leading-relaxed">
                    When someone forks your project or responds to a challenge, you'll see it here.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-zinc-800/30">
                  {notifications.map(notif => (
                    <NotificationItem
                      key={notif.id}
                      notification={notif}
                      onClickNotif={handleClickNotification}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="border-t border-zinc-800/50 px-4 py-2.5">
                <button
                  onClick={() => { navigate('/notifications'); setIsOpen(false); }}
                  className="w-full flex items-center justify-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors py-0.5"
                >
                  View all notifications
                  <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          15% { transform: rotate(12deg); }
          30% { transform: rotate(-10deg); }
          45% { transform: rotate(8deg); }
          60% { transform: rotate(-6deg); }
          75% { transform: rotate(3deg); }
        }
        .animate-wiggle {
          animation: wiggle 0.6s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default NotificationBell;